import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Form } from "antd";
import { KnownEmptyDefaults, isServiceAllFieldsByCdnState } from "@devowl-wp/api-real-cookie-banner";
import { useWhiteListedFormItems } from "../../../../hooks/form/useWhiteListedFormItems.js";
import { useServiceCdnState } from "../../../../hooks/service/useServiceCdnState.js";
import { useStores } from "../../../../store/root.js";
/**
 * Wraps form fields to control their visibility based on the CDN field state.
 *
 * Note: This component relies on Form Instance context and should be used as a child of an Ant Design Form component.
 * It accesses form data and methods through the Form.useFormInstance() hook, which requires it to be nested within a Form.
 * Placing this component outside of a Form context will result in errors or unexpected behavior.
 *
 * @param props The properties passed to the component. Includes:
 * - children: The content to be conditionally displayed based on the CDN field state.
 *
 * @returns The component to conditionally display the children based on the CDN field state.
 */ const FormFieldVisibilityWrapperByCdn = ({ children })=>{
    const cdnFieldState = useServiceCdnState();
    const isFormFullyVisible = isServiceAllFieldsByCdnState(cdnFieldState);
    if (isFormFullyVisible) {
        return children;
    }
    return /*#__PURE__*/ _jsx("div", {
        style: {
            display: "none"
        },
        children: children
    });
};
/**
 * Wraps form fields to control their visibility based on various conditions including user permissions,
 * whitelist configurations, and URL parameters. This component is designed to conditionally render
 * its children based on the specified conditions.
 *
 * The visibility of the fields is determined by checking if the field names are listed in a whitelist,
 * the user's role has the permission (if a specific role is required), and if the field names are
 * allowed by URL parameters. Additionally, visibility can be forcibly hidden regardless of other
 * conditions.
 *
 * @param props The properties passed to the component. Includes:
 * - fieldNames: A single field name or an array of field names to check against whitelists.
 * - whiteList: An array of field names that are explicitly allowed to be visible.
 * - permissionCheck: An object containing a role to check against the user's role, a strategy ('all' or 'any'),
 *                    and a roleWhiteList which is an array of field names allowed for the role.
 * - children: The content to be conditionally displayed based on the visibility checks.
 * - forceHidden: A boolean to forcibly hide the content regardless of other conditions.
 * - forceHiddenFormCallback: A special case callback function to check for forceHidden state.
 *                            The current form instance is available within callback function.
 *
 * Utilizes `useStores` to access the authentication store for role-based permissions, and
 * `useWhiteListedFormItems` to get URL parameters allowed list.
 *
 * The logic for visibility is as follows:
 * - If the field names are in the general whitelist and the URL parameters whitelist, and the user's role
 *   matches the specified role (if any) and its associated fields are in the role's whitelist,
 *   the children are rendered.
 * - If any of the conditions fail, or `forceHidden` is true, the children are rendered but hidden.
 */ const FormFieldVisibilityWrapper = (props)=>{
    const { rcbAuthenticationStore } = useStores();
    const { fieldNames, whiteList, permissionCheck, children, forceHidden, forceHiddenFormCallback } = props;
    const form = Form.useFormInstance();
    const fieldNamesToCheck = typeof fieldNames === "string" ? [
        fieldNames
    ] : fieldNames;
    const isListed = isWhiteListed(fieldNamesToCheck, whiteList);
    let isAnyWhiteListFieldValue = true;
    let isAllowed = true;
    if (!!permissionCheck?.whenRole && rcbAuthenticationStore?.session?.user?.role === permissionCheck.whenRole) {
        // check if user has permission to see these fields
        const { roleWhiteList, strategy } = permissionCheck;
        isAllowed = isWhiteListed(fieldNamesToCheck, roleWhiteList, strategy || "all");
        // filter roleWhiteList to only include fieldNamesToCheck and check if any of them has a value
        const filteredRoleWhiteList = roleWhiteList.filter((fieldName)=>fieldNamesToCheck.includes(fieldName));
        isAnyWhiteListFieldValue = hasAnyFieldValue(filteredRoleWhiteList, form);
    }
    const urlParamsWhiteList = useWhiteListedFormItems();
    const isUrlParamsAllowed = isWhiteListed(fieldNamesToCheck, urlParamsWhiteList);
    let forceHiddenCallbackResult = false;
    if (forceHiddenFormCallback) {
        if (form) {
            forceHiddenCallbackResult = forceHiddenFormCallback(form);
        }
    }
    if (isListed && isAllowed && isUrlParamsAllowed && !forceHidden && !forceHiddenCallbackResult && isAnyWhiteListFieldValue) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    } else {
        return /*#__PURE__*/ _jsx("div", {
            style: {
                display: "none"
            },
            children: children
        });
    }
};
/**
 * Checks if the given field names are whitelisted according to the specified strategy.
 *
 * This function supports two strategies for checking the whitelist: 'any' and 'all'.
 * - 'any': Returns true if at least one of the specified field names is in the whitelist.
 * - 'all': Returns true only if all of the specified field names are in the whitelist.
 *
 * If the whitelist is not provided or is empty, the function defaults to considering
 * the field names as whitelisted, returning true.
 *
 * @param fieldNames An array of field names to check against the whitelist.
 * @param whiteList An array of field names that are considered whitelisted.
 * @param strategy Determines how the field names should be checked against the whitelist.
 *                 Defaults to 'any'.
 *
 * @returns A boolean indicating whether the field names are considered whitelisted according
 *          to the given strategy.
 */ function isWhiteListed(fieldNames, whiteList, strategy = "any") {
    let isWhiteListed1 = false;
    if (whiteList) {
        isWhiteListed1 = strategy === "all";
        for (const fieldName of fieldNames){
            if (strategy === "any") {
                isWhiteListed1 = isWhiteListed1 || whiteList.includes(fieldName);
            } else {
                isWhiteListed1 = isWhiteListed1 && whiteList.includes(fieldName);
            }
        }
    } else {
        isWhiteListed1 = true; // if no whitelist -> ok
    }
    return isWhiteListed1;
}
/**
 * Checks if the given field names have any non-empty values in the form.
 *
 * @param fieldNames An array of field names to check against the form.
 * @param form The form instance to check the field values against.
 *
 * @returns A boolean indicating whether the field names have any non-empty values in the form.
 */ function hasAnyFieldValue(fieldNames, form) {
    return fieldNames.some((fieldName)=>{
        const fieldValue = form?.getFieldValue(fieldName);
        return fieldValue && fieldValue !== KnownEmptyDefaults.QUILL_KNOWN_EMPTY && (!Array.isArray(fieldValue) || fieldValue.length > 0);
    });
}
export { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn };
